import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, debounceTime, distinctUntilChanged, firstValueFrom, map, takeUntil } from 'rxjs';
import { ALCANCE_LABELS, APP_DESTINO_LABELS, Comunicacion } from 'src/app/models/Comunicacion';
import { ComunicacionService } from 'src/app/services/comunicacion.service';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { normalizeText } from 'src/app/shared/text.util';

@Component({
    selector: 'app-comunicaciones',
    templateUrl: './comunicaciones.component.html',
    styleUrls: ['./comunicaciones.component.css'],
    standalone: false
})
export class ComunicacionesComponent implements OnInit, OnDestroy {
  private _authService = inject(AuthService);
  private _comunicacionService = inject(ComunicacionService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);

  comunicaciones: Comunicacion[] = [];
  comunicacionesFiltradas$?: Observable<Comunicacion[]>;
  searchTerm$ = new BehaviorSubject<string>('');
  error = signal<string>('');
  private OnDestroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    try {
      await this._loadingService.run(async () => {
        await this._authService.ensureSession();
        this.comunicaciones = await firstValueFrom(this._comunicacionService.getComunicaciones()) ?? [];
        this.filterList();
      });
    } catch (e: any) {
      console.error(e);
      this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
      this.router.navigate(['/sign-in']);
    }
  }

  ngOnDestroy(): void {
    this.OnDestroy$.next();
    this.OnDestroy$.complete();
  }

  filterList(): void {
    this.comunicacionesFiltradas$ = this.searchTerm$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.OnDestroy$),
      map(term => {
        const t = normalizeText(term);

        return this.comunicaciones.filter(c => {
          const titulo = normalizeText(c.x_titulo);
          const alcance = normalizeText(this.getAlcanceLabel(c.x_alcance));
          const adminEmail = normalizeText(c.x_admin_email);
          const destinoEmail = normalizeText(c.x_destino_email);

          return (
            titulo.includes(t) ||
            alcance.includes(t) ||
            adminEmail.includes(t) ||
            destinoEmail.includes(t)
          );
        });
      })
    );
  }

  getAlcanceLabel(alcance?: string): string {
    return ALCANCE_LABELS[alcance ?? ''] ?? (alcance ?? '');
  }

  getAppDestinoLabel(app?: string): string {
    return APP_DESTINO_LABELS[app ?? ''] ?? (app ?? '');
  }

  getLeidoLabel(c: Comunicacion): string {
    const destinatarios = c.n_destinatarios ?? 0;
    const leidos = c.n_leidos ?? 0;

    return destinatarios === 1
      ? (leidos > 0 ? 'Leído' : 'No leído')
      : `Leído ${leidos}/${destinatarios}`;
  }

  back(){
    this._location.back();
  }
}
